import React, { useState, useEffect, useCallback } from "react";
import { Select, Spin, Alert } from "antd";
import { SyncOutlined, DownloadOutlined } from "@ant-design/icons";
import { format } from "date-fns";
import api from "../../../api";
import {
  FolderSLAResponse,
  FolderSLATabProps,
  MultireportTeam,
} from "../types/folderSla.types";
import FolderSLASummaryCards from "../cards/FolderSLASummaryCards";
import FolderSLAUserChart from "../charts/FolderSLAUserChart";
import FolderSLAUserCards from "../cards/FolderSLAUserCards";

const { Option } = Select;

const MultireportSLATab: React.FC<FolderSLATabProps> = ({
  period,
  dateRange,
  filterType,
}) => {
  const [selectedTeam, setSelectedTeam] = useState<MultireportTeam | "all">(
    "all"
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<FolderSLAResponse | null>(null);

  const buildQueryParams = useCallback(() => {
    const params: Record<string, string> = {};

    if (filterType === "preset") {
      params.period = period;
    } else if (filterType === "custom" && dateRange) {
      params.start_date = dateRange[0];
      params.end_date = dateRange[1];
    }

    return new URLSearchParams(params).toString();
  }, [filterType, period, dateRange]);

  const fetchData = useCallback(async () => {
    if (filterType === "custom" && !dateRange) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const queryString = buildQueryParams();
      let endpoint = `/metrics/sla/multireport/?${queryString}`;

      if (selectedTeam !== "all") {
        endpoint = `/metrics/sla/multireport/team/${selectedTeam}/?${queryString}`;
      }

      const response = await api.get(endpoint);
      setData(response.data);
    } catch (err: any) {
      console.error("Error fetching Multireport SLA data:", err);
      setError(
        `Error al cargar métricas de Multireport: ${err.response?.data?.message || err?.message}`
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedTeam, buildQueryParams, filterType, dateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTeamChange = (value: MultireportTeam | "all") => {
    setSelectedTeam(value);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const exportToCsv = () => {
    if (!data || !data.by_user) return;

    const headers = [
      "user_name",
      "user_email",
      "total_folders",
      "completed_folders",
      "before_cutoff",
      "after_cutoff",
      "non_compliant",
      "pending",
      "compliance_pct",
      "before_cutoff_pct",
      "after_cutoff_pct",
      "non_compliant_pct",
    ];

    const rows = data.by_user.map((user) =>
      headers.map((h) => (user as any)[h] ?? "").join(",")
    );

    const csvContent = [headers.join(","), ...rows].join("\r\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `multireport-sla-${format(new Date(), "yyyy-MM-dd")}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Equipo:</span>
          <Select
            className="w-48"
            onChange={handleTeamChange}
            value={selectedTeam}
          >
            <Option value="all">Todos</Option>
            <Option value="operations">Operations</Option>
            <Option value="operations_assistant">Operations Assistant</Option>
          </Select>
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleRefresh}
            disabled={loading || refreshing}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm leading-5 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            <SyncOutlined spin={refreshing} className="mr-1" />
            Actualizar
          </button>
          <button
            onClick={exportToCsv}
            disabled={loading || !data}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm leading-5 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            <DownloadOutlined className="mr-1" />
            Exportar
          </button>
        </div>
      </div>

      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
        />
      )}

      {loading ? (
        <div className="flex justify-center items-center py-24">
          <Spin size="large" />
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <FolderSLASummaryCards data={data?.overall || null} />

          {/* User Compliance Chart */}
          <div className="bg-white rounded-lg shadow p-4">
            <h2 className="text-lg font-medium mb-4 text-gray-700">
              Cumplimiento por Usuario
              {selectedTeam !== "all" && (
                <span className="text-sm text-gray-500 ml-2">
                  - {selectedTeam === "operations" ? "Operations" : "Operations Assistant"}
                </span>
              )}
            </h2>
            <FolderSLAUserChart data={data?.by_user || []} />
          </div>

          {/* Individual Performance Cards */}
          <FolderSLAUserCards
            data={data?.by_user || []}
            title={
              selectedTeam !== "all"
                ? `Rendimiento Individual - ${selectedTeam === "operations" ? "Operations" : "Operations Assistant"}`
                : "Rendimiento Individual"
            }
          />
        </>
      )}
    </div>
  );
};

export default MultireportSLATab;
